// app/frontend/components/EventForm/EventForm.tsx
import { useState, useEffect, useMemo, useRef } from 'react';
import { useForm } from '@mantine/form';
import { Container, Paper, Title, Alert, Box } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconInfoCircle, IconCheck } from '@tabler/icons-react';
import { useEditor } from '@tiptap/react';
import Highlight from '@tiptap/extension-highlight';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Heading from '@tiptap/extension-heading';
import Link from '@tiptap/extension-link';
import { useMediaQuery } from '@mantine/hooks';
import { useUser } from '../../src/context/UserContext';
import FacebookEventImporter from '../FacebookEventImporter';

// Import all sections
import EventInfoSection from './sections/EventInfoSection';
import LocationInfoSection from './sections/LocationInfoSection';
import DateTimeSection from './sections/DateTimeSection';
import EventDetailsSection from './sections/EventDetailsSection';
import ArtistSelectionSection from './sections/ArtistSelectionSection';
import LinksSection from './sections/LinksSection';
import EventFormButtons from './sections/EventFormButtons';

// Import custom hooks
import { useSlugValidation } from './hooks/useSlugValidation';
import { useTimezones } from './hooks/useTimezones';
import { useArtists } from './hooks/useArtists';
import { useEventSubmission } from './hooks/useEventSubmission';
import { useEventValidation } from './hooks/useEventValidation';

// Import utilities
import { filterUserOrganizers, sortByName } from './utils/formHelpers';
import { transformEditEventData, getCreateEventDefaults, isEditMode } from './utils/editEventTransform';

// Import types
import type { EventFormProps, EventFormValues, User, ImportedEventData } from './types';

const EventForm: React.FC<EventFormProps> = ({
  event,
  artists: propArtists,
  event_types: propEventTypes,
  dance_styles: propDanceStyles,
  event_frequencies: propEventFrequencies,
  organizers: propOrganizers,
  errors,
  areas: propAreas,
  cities: propCities,
  timezones,
}) => {
  // -------------------------------
  // State & Context
  // -------------------------------
  const [isRecurring, setIsRecurring] = useState(false);
  const [seoImagePreview, setSeoImagePreview] = useState<string | null>(event.event_image_url);
  const [croppedImage, setCroppedImage] = useState<File | null>(null);

  const { user } = useUser() as { user: User | null };
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isDraft = event.event_status_id === 3;
  const validationRulesSet = useRef(false);

  // Determine initial values based on create vs edit mode
  const initialFormValues = useMemo(() => {
    if (isEditMode(event)) {
      console.log('🔄 Edit mode detected, transforming event data:', event);
      return transformEditEventData(event);
    } else {
      console.log('✨ Create mode detected, using default values');
      return getCreateEventDefaults();
    }
  }, [event]);

  // Extract initial values for state
  const [address, setAddress] = useState(initialFormValues.address);
  const [timezone, setTimezone] = useState(initialFormValues.timezone);

  // console.log('🌍🌍🌍 Timezone debug:', {
  //   originalEvent: event.timezone,
  //   transformedValue: initialFormValues.timezone,
  //   timezoneState: timezone,
  // });

  // Create a simple validation first, then update it
  const [currentValidationRules, setCurrentValidationRules] = useState<any>({});

  // -------------------------------
  // Form Setup (create first, before other hooks)
  // -------------------------------
  const form = useForm<EventFormValues>({
    initialValues: initialFormValues,
    validate: currentValidationRules,
    validateInputOnChange: true,
  });

  // -------------------------------
  // Custom Hooks (now form exists)
  // -------------------------------
  const { slug, setSlug, slugStatus } = useSlugValidation(initialFormValues.slug, form.values.name);
  const { timezoneOptions, isLoadingTimezones } = useTimezones(timezones);
  const { mergedArtists, isLoadingArtists, artistOptionsLoaded, fetchAllArtists } = useArtists(propArtists);

  // -------------------------------
  // Rich Text Editor
  // -------------------------------
  const editor = useEditor({
    extensions: [
      StarterKit,
      Highlight,
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Heading.configure({ levels: [1, 2, 3] }),
      Link.configure({ openOnClick: true }),
    ],
    content: form.values.description,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      form.setFieldValue('description', html);
    },
  });

  // -------------------------------
  // Custom Validation (now editor is available)
  // -------------------------------
  // Get full validation rules
  const { validationRules } = useEventValidation({ isDraft, editor, event });

  // Set validation rules after editor and validation rules are ready
  useEffect(() => {
    if (editor && !validationRulesSet.current) {
      console.log('✅ Setting up validation rules');
      setCurrentValidationRules(validationRules);
      validationRulesSet.current = true;
    }
  }, [editor]);

  // -------------------------------
  // Event Submission Hook
  // -------------------------------
  const { submitEvent, isLoading } = useEventSubmission({
    form,
    event,
    editor,
    user,
    croppedImage,
  });

  // -------------------------------
  // Derived Data
  // -------------------------------
  const filteredOrganizers = useMemo(() => {
    const filtered = filterUserOrganizers(propOrganizers || [], user);
    return sortByName(filtered);
  }, [propOrganizers, user]);

  // -------------------------------
  // Utility Functions
  // -------------------------------
  function validateURL(input: string) {
    const urlPattern = /^(http:\/\/|https:\/\/)/i;
    return urlPattern.test(input);
  }

  // -------------------------------
  // Event Handlers
  // -------------------------------
  const handleImageChange = (file: File) => {
    form.setFieldValue('event_image', file);
    setSeoImagePreview(URL.createObjectURL(file));
  };

  const handleFacebookImport = async (importedData: ImportedEventData) => {
    // Update form values with imported data
    if (importedData.name) {
      form.setFieldValue('name', importedData.name);
    }

    if (importedData.description) {
      form.setFieldValue('description', importedData.description);
      if (editor) {
        editor.commands.setContent(importedData.description);
      }
    }

    if (importedData.address) {
      form.setFieldValue('address', importedData.address);
      setAddress(importedData.address);
    }

    if (importedData.timezone) {
      form.setFieldValue('timezone', importedData.timezone);
      setTimezone(importedData.timezone);
    }

    // Handle dates and times
    if (importedData.event_start_date) {
      form.setFieldValue('event_start_date', importedData.event_start_date);
    }
    if (importedData.event_start_time) {
      form.setFieldValue('event_start_time', importedData.event_start_time);
    }
    if (importedData.event_end_date) {
      form.setFieldValue('event_end_date', importedData.event_end_date);
    }
    if (importedData.event_end_time) {
      form.setFieldValue('event_end_time', importedData.event_end_time);
    }

    // Handle detected data
    if (importedData.detected_artist_ids && importedData.detected_artist_ids.length > 0) {
      if (!artistOptionsLoaded) {
        await fetchAllArtists();
      }
      form.setFieldValue('artistIds', importedData.detected_artist_ids);
    }

    if (importedData.detected_dance_style_ids && importedData.detected_dance_style_ids.length > 0) {
      form.setFieldValue('dance_style_ids', importedData.detected_dance_style_ids);
    }

    if (importedData.detected_event_type_ids && importedData.detected_event_type_ids.length > 0) {
      form.setFieldValue('event_type_ids', importedData.detected_event_type_ids);
    }

    if (importedData.facebook_url) {
      form.setFieldValue('facebook_url', importedData.facebook_url);
    }

    if (importedData.spans_next_day !== undefined) {
      form.setFieldValue('spans_next_day', importedData.spans_next_day ? 'true' : 'false');
    }
  };

  // -------------------------------
  // Effects
  // -------------------------------
  useEffect(() => {
    form.setFieldValue('slug', slug);
  }, [slug, form]);

  useEffect(() => {
    setIsRecurring(form.values.is_recurring === 'true');
  }, [form.values.is_recurring]);

  // Status mapping
  const statusMapping: Record<number, { status: string; color: string; icon: JSX.Element }> = {
    1: { status: 'Active', color: 'green', icon: <IconCheck size={16} /> },
    2: { status: 'Pending', color: 'yellow', icon: <IconAlertTriangle size={16} /> },
    3: { status: 'Draft', color: 'blue', icon: <IconInfoCircle size={16} /> },
  };

  const eventStatus =
    event.event_status_id && statusMapping[event.event_status_id]
      ? statusMapping[event.event_status_id]
      : { status: 'None', color: 'gray', icon: <IconInfoCircle size={16} /> };

  // -------------------------------
  // Render
  // -------------------------------
  return (
    <>
      <Notifications position="top-right" />
      <Container size="sm" style={{ minHeight: 'auto' }} px={isMobile ? 0 : undefined}>
        <Paper
          p={isMobile ? '0rem' : '0px'}
          shadow="xs"
          mb="0rem"
          style={{ borderRadius: '8px', backgroundColor: '#3d3c3e' }}
        >
          {/* Event Status Alert */}
          {event.event_status_id && (
            <Alert
              icon={eventStatus.icon}
              title={`Event Status: ${eventStatus.status}`}
              color={eventStatus.color}
              variant="light"
              mb="md"
            />
          )}

          {/* Title */}
          <Title order={3} c="#E4E4E7" mt="md" mx="sm">
            {isEditMode(event) ? 'Edit Event' : 'Create New Event'}
          </Title>

          {/* Facebook Event Importer - Only show for create mode */}
          {!isEditMode(event) && <FacebookEventImporter onEventImported={handleFacebookImport} disabled={isLoading} />}

          {/* Form */}
          <form>
            {/* Event Info Section */}
            <EventInfoSection form={form} slug={slug} setSlug={setSlug} organizers={filteredOrganizers} user={user} />

            {/* Location Info Section */}
            <LocationInfoSection
              form={form}
              address={address}
              setAddress={setAddress}
              timezone={timezone}
              setTimezone={setTimezone}
              areas={propAreas || []}
              cities={propCities || []}
              railsTimezoneData={timezoneOptions}
            />

            {/* Date & Time Section */}
            <DateTimeSection
              form={form}
              isDraft={isDraft}
              isRecurring={isRecurring}
              setIsRecurring={setIsRecurring}
              eventFrequencies={propEventFrequencies || []}
            />

            {/* Event Details Section */}
            <EventDetailsSection
              form={form}
              isDraft={isDraft}
              editor={editor}
              eventTypes={propEventTypes}
              danceStyles={propDanceStyles}
              eventFrequencies={propEventFrequencies}
              seoImagePreview={seoImagePreview}
              onImageChange={handleImageChange}
            />

            {/* Artist Selection Section */}
            <ArtistSelectionSection
              form={form}
              artists={mergedArtists}
              isLoadingArtists={isLoadingArtists}
              artistOptionsLoaded={artistOptionsLoaded}
              fetchAllArtists={fetchAllArtists}
            />

            {/* Links Section */}
            <LinksSection form={form} />

            {/* Error Display */}
            {errors && Object.keys(errors).length > 0 && (
              <Box color="red" mt="md">
                {Object.entries(errors).map(([field, messages]) => (
                  <div key={field}>
                    {field}: {messages.join(', ')}
                  </div>
                ))}
              </Box>
            )}

            {/* Form Buttons */}
            <EventFormButtons form={form} isLoading={isLoading} onSubmit={submitEvent} event={event} />
          </form>
        </Paper>
      </Container>
    </>
  );
};

export default EventForm;
